import { forwardRef, type ButtonHTMLAttributes } from "react";
import { cn } from "@/lib/cn";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "secondary" | "ghost" | "danger" | "outline";
  size?: "sm" | "md" | "lg";
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(
    {
      className,
      variant = "secondary",
      size = "md",
      loading,
      disabled,
      children,
      ...props
    },
    ref,
  ) {
    return (
      <button
        ref={ref}
        disabled={disabled || loading}
        className={cn(
          "inline-flex items-center justify-center gap-2 font-medium rounded transition-colors duration-100 select-none",
          "disabled:opacity-40 disabled:cursor-not-allowed",
          {
            primary: "bg-accent hover:bg-accent-hover text-white",
            secondary:
              "bg-bg-hover hover:bg-bg-active text-text-primary border border-border",
            ghost:
              "hover:bg-bg-hover text-text-secondary hover:text-text-primary",
            danger:
              "bg-danger/15 hover:bg-danger/25 text-danger border border-danger/20",
            outline:
              "border border-border-strong hover:border-border-accent text-text-primary hover:text-accent",
          }[variant],
          {
            sm: "text-xs px-2.5 py-1.5",
            md: "text-sm px-3.5 py-2",
            lg: "text-base px-5 py-2.5",
          }[size],
          className,
        )}
        {...props}
      >
        {loading && (
          <svg
            className="animate-spin w-3.5 h-3.5"
            viewBox="0 0 24 24"
            fill="none"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
            />
          </svg>
        )}
        {children}
      </button>
    );
  },
);
